import { useEffect } from "react";
import { Phone, Mail, MapPin, Clock, Facebook } from "lucide-react";
import QuoteForm from "@/components/QuoteForm";
import Reveal from "@/components/SectionReveal";
import { useSeo, localBusinessJsonLd } from "@/lib/seo";
import { SITE } from "@/data/site";

export default function ContactPage() {
  useSeo({
    title: "Contact Tampa Bay Pool Pros — Free Quotes & 24/7 Emergency",
    description:
      "Contact Tampa Bay Pool Pros for a free quote on pool cleaning, weekly pool service, green pool cleanup, filter & pump service, or 24/7 pool emergency repair. Call (727) 643-9404 or send us a message.",
    path: "/contact",
    image: SITE.logo,
    jsonLd: localBusinessJsonLd(),
  });
  useEffect(() => { window.scrollTo({ top: 0 }); }, []);

  return (
    <main className="bg-white">
      <section className="pt-32 sm:pt-44 pb-10 bg-gradient-to-br from-sky-50 via-white to-amber-50/40">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <Reveal>
            <p className="text-xs tracking-[0.22em] uppercase font-bold text-sky-700">
              Get in touch
            </p>
            <h1 className="mt-3 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 max-w-4xl">
              Free quotes. 24/7 pool emergency.
            </h1>
            <p className="mt-5 max-w-2xl text-slate-700 text-lg">
              Tell us about your pool — we&apos;ll get back to you fast with a no-obligation quote, or call us any
              time for emergencies.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 grid lg:grid-cols-5 gap-10">
          <Reveal direction="left" className="lg:col-span-2 space-y-6">
            <div className="rounded-3xl p-6 bg-slate-950 text-white">
              <p className="text-xs uppercase tracking-[0.22em] text-amber-400 font-bold">Call us</p>
              <a
                href={`tel:${SITE.phoneTel}`}
                className="mt-2 flex items-center gap-3 text-2xl font-heading font-bold"
                data-testid="contact-phone"
              >
                <Phone className="w-6 h-6 text-amber-300" /> {SITE.phoneDisplay}
              </a>
              <p className="mt-1 text-sm text-emerald-400 font-semibold">Available 24/7 for emergencies</p>
            </div>

            <div className="rounded-3xl p-6 bg-white border border-slate-100 shadow-sm">
              <p className="text-xs uppercase tracking-[0.22em] text-sky-700 font-bold">Other ways</p>
              <ul className="mt-3 space-y-3 text-sm">
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-sky-700" />
                  <a href={`mailto:${SITE.email}`} className="hover:text-sky-700">{SITE.email}</a>
                </li>
                <li className="flex items-center gap-2">
                  <Facebook className="w-4 h-4 text-sky-700" />
                  <a
                    href={SITE.facebook}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="hover:text-sky-700"
                  >
                    facebook.com/TampaBayPoolPros
                  </a>
                </li>
                <li className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-sky-700" />
                  <span>Serving the greater Tampa Bay area, FL</span>
                </li>
                <li className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-sky-700" />
                  <span>Open 24/7 for emergencies</span>
                </li>
              </ul>
            </div>
          </Reveal>

          <div className="lg:col-span-3">
            <QuoteForm />
          </div>
        </div>
      </section>
    </main>
  );
}
